import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConnectionQuality } from 'livekit-client';

import { IUser } from '../../../lib/interfaces';
import { isWeak } from '../callSignals';
import { RemoteSignals, SelfParticipant } from '../types';
import { DS } from '../designTokens';
import { usePatientName } from '../hooks/usePatientName';
import DeviceMenuButton from './DeviceMenuButton';

const OWN_BAR_HEIGHTS = [6, 10, 13, 17];
const PATIENT_BAR_HEIGHTS = [5, 9, 13, 16];

const barsFor = (quality: ConnectionQuality): number => {
  if (quality === ConnectionQuality.Excellent) return 4;
  if (quality === ConnectionQuality.Good) return 3;
  if (quality === ConnectionQuality.Poor) return 1;
  return 0;
};

const Bars = ({ filled, heights }: { filled: number; heights: number[] }) => (
  <Box
    aria-hidden
    sx={{
      display: 'flex',
      alignItems: 'flex-end',
      gap: '3px',
      height: heights[heights.length - 1],
      flex: 'none',
    }}
  >
    {heights.map((height, index) => (
      <Box
        key={height}
        sx={{
          width: 4,
          height,
          borderRadius: '2px',
          bgcolor: index < filled ? DS.deepTeal : DS.barEmpty,
        }}
      />
    ))}
  </Box>
);

const Card = ({
  background,
  children,
}: {
  background: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      flex: 'none',
      p: '15px 17px',
      borderRadius: '20px',
      bgcolor: background,
      boxShadow:
        '0 2px 8px rgba(36, 40, 41, 0.05), 0 1px 2px rgba(36, 40, 41, 0.04)',
      fontFamily: DS.fontBody,
      color: DS.softBlack,
    }}
  >
    {children}
  </Box>
);

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="h3"
    sx={{
      m: 0,
      mb: '10px',
      textTransform: 'uppercase',
      fontWeight: 900,
      fontSize: 12,
      letterSpacing: '0.08em',
      color: DS.muted,
    }}
  >
    {children}
  </Box>
);

const StateLine = ({ label, sub }: { label: string; sub?: string }) => (
  <Box sx={{ lineHeight: 1.35, minWidth: 0 }}>
    <Box sx={{ fontWeight: 700, fontSize: 13 }}>{label}</Box>
    {sub && (
      <Box
        sx={{
          fontSize: 11,
          color: DS.muted,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {sub}
      </Box>
    )}
  </Box>
);

const ownLabelKey = (quality: ConnectionQuality): string => {
  if (isWeak(quality)) return 'videoVisit.side.ownWeak';
  if (quality === ConnectionQuality.Unknown) return 'videoVisit.side.measuring';
  return 'videoVisit.side.ownStable';
};

const switchDeviceButtonSx = {
  position: 'relative',
  flex: 'none',
  p: '5px 10px',
  border: 'none',
  borderRadius: '999px',
  bgcolor: 'rgba(36,40,41,.06)',
  color: DS.muted,
  fontFamily: DS.fontBody,
  fontWeight: 700,
  fontSize: 11,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:hover': { bgcolor: DS.lineSoft },
  '&:focus-visible': {
    outline: `2px solid ${DS.freshTeal}`,
    outlineOffset: '2px',
  },
  // The design keeps the button small. This widens what can be clicked to the
  // 44px the design requires for hit targets.
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '-11px -4px',
  },
};

const OwnEquipmentCard = ({
  quality,
  microphoneName,
}: {
  quality: ConnectionQuality;
  microphoneName?: string;
}) => {
  const { t } = useTranslation();
  const weak = isWeak(quality);

  return (
    <Card background={weak ? DS.cardWeak : DS.card}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <SectionHeader>{t('videoVisit.side.ownTitle')}</SectionHeader>
        <DeviceMenuButton opens="down" sx={switchDeviceButtonSx}>
          {t('videoVisit.devices.switch')}
        </DeviceMenuButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
        <Bars filled={barsFor(quality)} heights={OWN_BAR_HEIGHTS} />
        <StateLine
          label={t(ownLabelKey(quality))}
          sub={weak ? t('videoVisit.side.ownWeakSub') : microphoneName}
        />
      </Box>
    </Card>
  );
};

type PatientState = {
  labelKey: string;
  subKey: string;
  warning: boolean;
};

const patientStateFor = ({
  quality,
  cameraOff,
  micOff,
}: RemoteSignals): PatientState => {
  if (quality === ConnectionQuality.Lost) {
    return {
      labelKey: 'videoVisit.side.patientLost',
      subKey: 'videoVisit.side.patientLostSub',
      warning: true,
    };
  }
  if (quality === ConnectionQuality.Poor) {
    return {
      labelKey: 'videoVisit.side.patientWeak',
      subKey: 'videoVisit.side.patientWeakSub',
      warning: true,
    };
  }
  if (cameraOff && micOff) {
    return {
      labelKey: 'videoVisit.side.patientNoMedia',
      subKey: 'videoVisit.side.cameraAndMicOff',
      warning: true,
    };
  }
  if (cameraOff) {
    return {
      labelKey: 'videoVisit.side.patientAudioOnly',
      subKey: 'videoVisit.side.cameraOff',
      warning: true,
    };
  }
  if (micOff) {
    return {
      labelKey: 'videoVisit.side.patientConnected',
      subKey: 'videoVisit.side.micOff',
      warning: true,
    };
  }
  return {
    labelKey: 'videoVisit.side.patientGood',
    subKey: 'videoVisit.side.devicesOk',
    warning: false,
  };
};

const ConnectionRow = ({
  filled,
  warning,
  label,
  sub,
}: {
  filled: number;
  warning: boolean;
  label: string;
  sub: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      p: '9px 11px',
      borderRadius: '14px',
      bgcolor: warning ? DS.dawnSurface : DS.linen,
    }}
  >
    <Bars filled={filled} heights={PATIENT_BAR_HEIGHTS} />
    <StateLine label={label} sub={sub} />
  </Box>
);

const PatientConnection = ({
  signals,
  patient,
}: {
  signals: RemoteSignals;
  patient: IUser;
}) => {
  const { t } = useTranslation();
  const patientName = usePatientName(patient);
  const state = patientStateFor(signals);

  return (
    <ConnectionRow
      filled={barsFor(signals.quality)}
      warning={state.warning}
      label={t(state.labelKey, { name: patientName })}
      sub={t(state.subKey, { name: patientName })}
    />
  );
};

type Props = {
  self: SelfParticipant;
  remote?: RemoteSignals;
  patient: IUser;
};

const SidePanel = ({ self, remote, patient }: Props) => {
  const { t } = useTranslation();
  const patientName = usePatientName(patient);

  return (
    <Box
      component="aside"
      aria-label={t('videoVisit.side.label')}
      sx={{
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        gap: 1.5,
        minHeight: 0,
      }}
    >
      <OwnEquipmentCard
        quality={self.quality}
        microphoneName={self.microphoneName}
      />
      <Card background={DS.card}>
        <SectionHeader>{t('videoVisit.side.patientTitle')}</SectionHeader>
        {remote ? (
          <PatientConnection signals={remote} patient={patient} />
        ) : (
          <ConnectionRow
            filled={0}
            warning={false}
            label={t('videoVisit.side.patientNotJoined')}
            sub={t('videoVisit.status.waitingForRemote', { name: patientName })}
          />
        )}
      </Card>
    </Box>
  );
};

export default SidePanel;
